import { Signal } from 'app/design/icons/signal';
import { Column, Row } from 'app/design/primitives/box';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { ListingType } from 'app/types/listing';
import type { SpecificNavigationProp } from 'app/types/navigation';
import type { User } from 'app/types/user';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

function SearchAssistantBottomSheet({
  itemName,
  navigation,
  onClose,
  open,
}: {
  itemName: string;
  navigation: SpecificNavigationProp<'all-listings'>;
  onClose: () => void;
  open: boolean;
}) {
  const { t } = useTranslation('all-listings');
  const openNewWithGuard = useOpenNewWithGuard();

  const launchSearch = () => {
    onClose();
    openNewWithGuard(navigation, {
      defaultValues: {
        type: ListingType.request,
        item: { name: itemName },
      },
      newListingSource: 'babillard_after_search_results',
    });
  };

  return (
    <BottomSheet onClose={onClose} open={open} showIndicator={false}>
      <Button
        accessibilityLabel={t('searchAssistant.close', { defaultValue: 'Fermer' })}
        hitSlop={20}
        onPress={onClose}
        pressableType={PressableType.GestureHandler}
        style={{ position: 'absolute', right: theme.spacing['spacing.general.x8'], top: 0, zIndex: 1 }}
        variant="plain"
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} />
      </Button>

      <Column
        gap="spacing.general.x8"
        style={{
          paddingHorizontal: theme.spacing['spacing.general.x8'],
          paddingBottom: theme.spacing['spacing.general.x8'],
        }}
      >
        <Text fontWeight="700" variant="headerXSmall">
          {t('searchAssistant.title')}
        </Text>

        <Column
          alignItems="center"
          justifyContent="center"
          style={{
            backgroundColor: theme.colors['surface.tertiary'],
            borderRadius: theme.borderRadii['radius.xl'],
            height: 180,
            width: '100%',
          }}
        >
          <Column
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: theme.colors.white,
              borderRadius: 40,
              height: 80,
              width: 80,
            }}
          >
            <Signal height={40} width={40} color={theme.colors['text.primary']} />
          </Column>
        </Column>

        <Text variant="bodyBase">
          <Trans
            components={{ strong: <Text fontWeight="700" variant="bodyBase" /> }}
            i18nKey="searchAssistant.body"
            ns="all-listings"
            values={{ itemName }}
          />
        </Text>
        <Text color="text.secondary" variant="bodyBase">
          {t('searchAssistant.bodyNote')}
        </Text>

        <Column alignItems="stretch" gap="spacing.general.x4" width="100%">
          <Button onPress={launchSearch} pressableType={PressableType.GestureHandler} variant="primary" width="100%">
            <Row alignItems="center" flex={1} gap="spacing.general.x2" justifyContent="center">
              <Signal color={theme.colors.white} height={24} width={24} />
              <Text
                color="white"
                flexShrink={1}
                fontWeight="700"
                numberOfLines={2}
                textAlign="center"
                variant="bodyBase"
              >
                {t('searchAssistant.confirm')}
              </Text>
            </Row>
          </Button>
          <Button onPress={onClose} pressableType={PressableType.GestureHandler} variant="secondary" width="100%">
            <Text fontWeight="700" textAlign="center" variant="bodyBase">
              {t('searchAssistant.dismiss')}
            </Text>
          </Button>
        </Column>
      </Column>
    </BottomSheet>
  );
}

interface SearchAssistantVisibilityOptions {
  canPublish: boolean;
  dismissedSearches: Set<string>;
  hasConfirmedEmptyResults: boolean;
  isLoggedIn: boolean;
  listingType: ListingType;
  promptToLookFurther: boolean;
  searchMatchesFilters: boolean;
  showConciergerie: boolean;
  trimmedSearch: string;
}

function shouldShowSearchAssistant({
  canPublish,
  dismissedSearches,
  hasConfirmedEmptyResults,
  isLoggedIn,
  listingType,
  promptToLookFurther,
  searchMatchesFilters,
  showConciergerie,
  trimmedSearch,
}: SearchAssistantVisibilityOptions) {
  return Boolean(
    showConciergerie &&
    isLoggedIn &&
    canPublish &&
    listingType === ListingType.offer &&
    trimmedSearch &&
    searchMatchesFilters &&
    !promptToLookFurther &&
    hasConfirmedEmptyResults &&
    !dismissedSearches.has(trimmedSearch),
  );
}

export function useSearchAssistantBottomSheet({
  canPublish,
  filterSearch,
  isLoggedIn,
  isFetching,
  isPending,
  itemsCount,
  listingType,
  navigation,
  numListingsTotal,
  promptToLookFurther,
  search,
  userFeatureFlags,
}: {
  canPublish: boolean;
  filterSearch: string;
  isLoggedIn: boolean;
  isFetching: boolean;
  isPending: boolean;
  itemsCount: number;
  listingType: ListingType;
  navigation: SpecificNavigationProp<'all-listings'>;
  numListingsTotal: number;
  promptToLookFurther: boolean;
  search: string;
  userFeatureFlags?: User['featureFlags'];
}) {
  const trimmedSearch = search.trim();
  const showConciergerie = isFeatureEnabled(FeatureFlag.conciergerie, userFeatureFlags);
  const [dismissedSearches, setDismissedSearches] = useState<Set<string>>(() => new Set());

  const searchMatchesFilters = trimmedSearch.length > 0 && trimmedSearch === filterSearch.trim();
  const hasConfirmedEmptyResults =
    searchMatchesFilters && !isPending && !isFetching && itemsCount === 0 && numListingsTotal === 0;

  const visible = shouldShowSearchAssistant({
    canPublish,
    dismissedSearches,
    hasConfirmedEmptyResults,
    isLoggedIn,
    listingType,
    promptToLookFurther,
    searchMatchesFilters,
    showConciergerie,
    trimmedSearch,
  });
  const activeSearch = visible ? trimmedSearch : '';

  const onClose = () => {
    if (activeSearch) {
      setDismissedSearches((searches) => new Set(searches).add(activeSearch));
    }
  };

  return {
    bottomSheet: visible ? (
      <SearchAssistantBottomSheet itemName={activeSearch} navigation={navigation} onClose={onClose} open={visible} />
    ) : null,
  };
}
